import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutGrid, Users, CalendarCheck, CreditCard, UserCircle, Lock, ShieldCheck, EyeOff, Loader2, LogOut, Building2, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/site/Logo";
import { useEffect, useState } from "react";
import { fetchAdminOtpStatus, logoutAdminOtp, clearAdminSession } from "@/lib/api";
import AdminNotificationBell from "@/components/admin/AdminNotificationBell";

const NAV = [
    { to: "/admin/overview", label: "Overview", icon: LayoutGrid },
    { to: "/admin/leads", label: "Leads", icon: Users },
    { to: "/admin/kunde", label: "Kundevirksomheder", icon: Building2 },
    { to: "/admin/ai-brancher", label: "AI-brancher", icon: BookOpen },
    { to: "/admin/planning", label: "Planlægning", icon: CalendarCheck },
    { to: "/admin/payments", label: "Betalinger", icon: CreditCard },
    { to: "/admin/customers", label: "Kunder", icon: UserCircle },
];

export default function AdminLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, loading } = useAuth();
    const [otpVerified, setOtpVerified] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate("/admin/login", { replace: true, state: { from: "/admin/overview" } });
            return;
        }
        if (!user.is_super_admin) {
            navigate("/dashboard", { replace: true });
            return;
        }
        // Already on the 2FA page? Skip the check (the page handles its own state).
        if (location.pathname === "/admin/2fa") {
            setChecking(false);
            return;
        }
        let alive = true;
        (async () => {
            try {
                const res = await fetchAdminOtpStatus();
                if (!alive) return;
                if (res?.verified) {
                    setOtpVerified(true);
                } else {
                    clearAdminSession();
                    navigate("/admin/2fa", { replace: true, state: { from: location.pathname } });
                }
            } catch {
                if (!alive) return;
                clearAdminSession();
                navigate("/admin/2fa", { replace: true, state: { from: location.pathname } });
            } finally {
                if (alive) setChecking(false);
            }
        })();
        return () => { alive = false; };
    }, [user, loading, navigate, location.pathname]);

    const handleLockAdmin = async () => {
        try { await logoutAdminOtp(); } catch { /* ignore */ }
        clearAdminSession();
        navigate("/admin/2fa", { replace: true });
    };

    if (loading || checking) {
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
    }

    // /admin/2fa renders without sidebar/topbar — just the Outlet
    if (location.pathname === "/admin/2fa") {
        return <Outlet />;
    }

    if (!otpVerified) {
        // Still redirecting — show spinner so we don't flash the layout
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
    }

    return (
        <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
            {/* Sidebar */}
            <aside className="w-60 bg-white border-r border-slate-200 flex flex-col shrink-0">
                <div className="px-5 py-5 border-b border-slate-100">
                    <Logo />
                </div>
                <nav className="flex-1 p-3 space-y-1">
                    {NAV.map(({ to, label, icon: Icon }) => (
                        <NavLink
                            key={to}
                            to={to}
                            data-testid={`admin-nav-${label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${
                                    isActive ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-600 hover:bg-slate-50"
                                }`
                            }
                        >
                            <Icon className="w-4 h-4" />
                            {label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-slate-100 text-xs">
                    <p className="text-slate-500 font-medium mb-1">Systemstatus</p>
                    <p className="text-emerald-600 flex items-center gap-1.5 mb-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" /> Alle systemer kører
                    </p>
                    <div className="space-y-1.5 text-slate-400">
                        <p className="flex justify-between"><span>Miljø</span><span className="text-slate-600">Production</span></p>
                        <p className="flex justify-between"><span>Version</span><span className="text-slate-600">v2.48.1</span></p>
                        <p>Sidst opdateret</p>
                        <p className="text-slate-600">24. maj 2025 08:12</p>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top bar */}
                <header className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-base font-semibold text-slate-900">Owner Admin Konsol</h1>
                            <span className="text-[10px] uppercase tracking-wider bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full font-bold">Kun for ejere</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="hidden md:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100"><Lock className="w-3 h-3" />2FA aktiveret</span>
                        <span className="hidden md:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100"><ShieldCheck className="w-3 h-3" />Kun ejeradgang</span>
                        <span className="hidden lg:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100"><EyeOff className="w-3 h-3" />Fortrolige data beskyttet</span>
                        <button onClick={handleLockAdmin} className="hidden md:inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition" data-testid="admin-lock-btn"><LogOut className="w-3 h-3" />Lås admin</button>
                        <AdminNotificationBell />
                        <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 inline-flex items-center justify-center text-white font-semibold text-sm">{(user?.name || "X").slice(0, 1).toUpperCase()}</div>
                            <div className="hidden sm:block">
                                <p className="text-sm font-medium text-slate-900 leading-tight">{user?.name || "Xenius"}</p>
                                <p className="text-[11px] text-slate-500 leading-tight">Ejer</p>
                            </div>
                        </div>
                    </div>
                </header>

                <main className="flex-1 p-6 overflow-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
